import { useCallback } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { datadogRum } from '@datadog/browser-rum';
import { datadogLogs } from '@datadog/browser-logs';
import { fetchAuthSession, fetchUserAttributes, getCurrentUser, signOut } from '@aws-amplify/auth';
import { userAtom } from '@tymely/atoms';
import { useSetAlert } from '@tymely/services';

export const useUser = () => useAtomValue(userAtom);
export const useSetUser = () => useSetAtom(userAtom);

const groupToRole = (group: string) => {
    switch (group) {
        case 'admins':
            return 'admin';
        case 'external-analysts':
            return 'external-analyst';
        case 'agents':
            return 'agent';
        default:
            return null;
    }
};

export const useAuthenticate = () => {
    const user = useUser();
    const setUser = useSetUser();
    const setAlert = useSetAlert();

    return {
        authenticate: useCallback(async () => {
            if (user) {
                return;
            }

            const cognitoUser = await getCurrentUser();
            const session = await fetchAuthSession({ forceRefresh: true });
            if (!session) return signOut();
            const userGroups = (session.tokens?.accessToken.payload['cognito:groups'] as string[]) || [];
            const userAttributes = await fetchUserAttributes();

            if (!userAttributes.email) {
                setAlert('User is missing an email address', 'error');
                return;
            }

            setUser({
                username: cognitoUser.username,
                isAdmin: userGroups.includes('admins'),
                roles: userGroups.map(groupToRole).filter(Boolean),
                email: userAttributes.email,
            });
            if (cognitoUser.username) {
                datadogRum.setUser({
                    name: cognitoUser.username,
                });
                datadogLogs.setGlobalContextProperty('username', cognitoUser.username);
            }
        }, [setUser, user]),
        user,
    };
};

export const SignOut = () => signOut({ global: true }).then(() => window.location.reload());
